import React, { useMemo, useState } from "react";

function ShapeIcon({ shape, filled }) {
  // Pleines (bonnes)
  const filledMap = {
    triangle: "▲",
    square: "■",
    pentagon: "⬟",
    hexagon: "⬢",
  };

  // Creuses (leurres) : volontairement plus différentes + “beaucoup de côtés”
  // (les glyphes unicode ne sont pas parfaits, mais ça marche bien en UI)
  const hollowMap = {
    circle: "○",
    octagon: "⯃",   // white octagon
    nonagon: "⬡",   // hollow hexagon-like (visuellement "beaucoup de côtés")
    diamond: "◇",
  };

  const char = filled ? filledMap[shape] : hollowMap[shape];

  return (
    <span
      className={[
        "text-5xl leading-none",
        filled ? "text-white" : "text-white/55",
      ].join(" ")}
      aria-label={shape}
    >
      {char ?? (filled ? "⬢" : "⬡")}
    </span>
  );
}

// Fisher–Yates shuffle
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function isStrictPlusOneProgression(nums) {
  if (!Array.isArray(nums) || nums.length !== 4) return false;
  for (let i = 1; i < nums.length; i++) {
    if (nums[i] !== nums[i - 1] + 1) return false;
  }
  return true;
}

export default function OrigamiCardsPuzzle({
  onSuccess,
  onAttempt, // optional: ({ ok: boolean }) => void
}) {
  const baseCards = useMemo(
    () => [
      // Face A = bonne (pleine), Face B = leurre (creuse + côtés “loin”)
      // Leurres choisis pour NE PAS pouvoir former une suite +1 entre eux :
      // sides = 8, 11, 14, 20 (pas de 8-9-10-11 etc)
      {
        id: "c1",
        a: { shape: "triangle", sides: 3, filled: true },
        b: { shape: "octagon", sides: 8, filled: false },
      },
      {
        id: "c2",
        a: { shape: "square", sides: 4, filled: true },
        b: { shape: "nonagon", sides: 11, filled: false },
      },
      {
        id: "c3",
        a: { shape: "pentagon", sides: 5, filled: true },
        b: { shape: "diamond", sides: 14, filled: false }, // “4 côtés” en vrai, mais visuellement différent + on force sides=14 pour le puzzle
      },
      {
        id: "c4",
        a: { shape: "hexagon", sides: 6, filled: true },
        b: { shape: "circle", sides: 20, filled: false },
      },
    ],
    []
  );

  // Mélange + faces initiales random (certaines sur le leurre)
  const initial = useMemo(() => {
    const shuffled = shuffle(baseCards);
    const face = {};
    for (const c of shuffled) {
      face[c.id] = Math.random() > 0.5; // true = a, false = b
    }
    return { shuffled, face };
  }, [baseCards]);

  const [cards, setCards] = useState(initial.shuffled);
  const [faceUp, setFaceUp] = useState(initial.face);
  const [dragId, setDragId] = useState(null);
  const [feedback, setFeedback] = useState("");

  function getVisibleSide(card) {
    return faceUp[card.id] ? card.a : card.b;
  }

  function flipCard(id) {
    setFaceUp((prev) => ({ ...prev, [id]: !prev[id] }));
    setFeedback("");
  }

  function onDragStart(id) {
    setDragId(id);
    setFeedback("");
  }

  function onDropOn(targetId) {
    if (!dragId || dragId === targetId) return;

    setCards((prev) => {
      const a = prev.findIndex((c) => c.id === dragId);
      const b = prev.findIndex((c) => c.id === targetId);
      if (a === -1 || b === -1) return prev;

      const next = [...prev];
      const [moved] = next.splice(a, 1);
      next.splice(b, 0, moved);
      return next;
    });

    setDragId(null);
  }

  function checkSolution() {
    setFeedback("");

    const visible = cards.map(getVisibleSide);

    // On vérifie la logique : progression +1 des "sides"
    const sides = visible.map((s) => Number(s.sides));
    const okProgression = isStrictPlusOneProgression(sides);

    // Optionnel mais utile : on s’assure aussi que ce sont bien des faces "pleines"
    // (sinon un joueur pourrait tenter de “forcer” une logique sur des leurres si tu changes les sides plus tard)
    const allFilled = visible.every((s) => s.filled === true);

    const ok = okProgression && allFilled;

    onAttempt?.({ ok });

    if (!ok) {
      setFeedback(
        "Rien ne se déclenche. On dirait qu’il manque un ordre évident… et qu’une face est peut-être trompeuse."
      );
      return;
    }

    setFeedback("✅ Ça réagit. Le terminal déverrouille un message.");
    onSuccess?.();
  }

  function resetPuzzle() {
    const shuffled = shuffle(baseCards);
    const face = {};
    for (const c of shuffled) face[c.id] = Math.random() > 0.5;

    setCards(shuffled);
    setFaceUp(face);
    setDragId(null);
    setFeedback("");
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-200">
        Des symboles sont encore visibles sur l'écran du terminal.
        Il semblerait qu’il faille remettre ces formes dans le bon ordre…
        Elles ont deux côtés. Il doit bien y avoir une logique.
      </p>

      <div className="flex flex-wrap gap-3">
        {cards.map((card) => {
          const side = getVisibleSide(card);

          return (
            <div
              key={card.id}
              draggable
              onDragStart={() => onDragStart(card.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDropOn(card.id)}
              className={[
                "w-[130px] select-none cursor-grab active:cursor-grabbing",
                "rounded-2xl border p-4",
                "bg-black/40 border-white/10 hover:border-white/20 transition",
              ].join(" ")}
              title="Glisse pour réordonner"
            >
              <button
                type="button"
                onClick={() => flipCard(card.id)}
                className="w-full text-center"
                title="Cliquer pour retourner"
              >
                <div className="flex justify-center">
                  <ShapeIcon shape={side.shape} filled={side.filled} />
                </div>

                <div className="mt-3 text-[10px] text-slate-500">
                  ↺ cliquer · ↔ glisser
                </div>
              </button>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-slate-500">Cliquez sur une carte pour la retourner. Glissez et déposer sur une autre carte pour les réordonner.</p>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={checkSolution}
          className="rounded-xl bg-indigo-600 hover:bg-indigo-500 px-4 py-2 text-xs font-semibold"
        >
          Vérifier
        </button>

        <button
          type="button"
          onClick={resetPuzzle}
          className="rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-2 text-xs font-semibold"
          title="Re-mélanger les cartes"
        >
          Re-mélanger
        </button>

        {feedback && <p className="text-xs text-amber-200">{feedback}</p>}
      </div>
    </div>
  );
}
